import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { ErrorMessage } from '@blockpulse3/ui/commons';

import { schema } from './schema';
import { IUpdatePasswordForm } from './types';

type Props = {
  onSubmit: (data: IUpdatePasswordForm) => void;
};

/**
 * UpdatePasswordForm.
 * Form to update the password of the user.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function UpdatePasswordForm({ onSubmit }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSecurity = useTranslations('UserSecurity');

  /* ** Control if form is "touched" (!= changed) ** */
  const [isTouched, setIsTouched] = useState<boolean>(false);
  /* ** Update password form ** */
  const { register, formState, handleSubmit } = useForm<IUpdatePasswordForm>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  /* ** Submit button disabled state ** */
  const isDisabled = useMemo(() => {
    return !formState.isValid || !isTouched;
  }, [isTouched, formState.isValid]);

  const handleFormTouched = (): void => {
    if (!isTouched) {
      setIsTouched(true);
    }
  };

  const handleFormSubmit: SubmitHandler<IUpdatePasswordForm> = (data: IUpdatePasswordForm) => {
    onSubmit(data);
  };

  return (
    <form onChange={handleFormTouched} onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }}>
        <Text flexBasis={{ lg: 250 }} fontSize="lg" fontWeight="medium">
          {i18nSecurity('ChangePassword')}
        </Text>
        <Card maxW={{ lg: '3xl' }} variant="divider-bottom" w="full">
          <CardBody>
            <Stack spacing="6">
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors.currentPassword}>
                  <FormLabel htmlFor="password">{i18nSecurity('CurrentPassword')}</FormLabel>
                  <Input
                    id="password"
                    type="password"
                    w={{ base: 'full', md: '49%' }}
                    {...register('currentPassword')}
                  />
                  <ErrorMessage error={formState.errors.currentPassword} />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors.newPassword}>
                  <FormLabel htmlFor="newPassword">{i18nSecurity('NewPassword')}</FormLabel>
                  <Input id="newPassword" type="password" {...register('newPassword')} />
                  <ErrorMessage error={formState.errors.newPassword} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors.confirm}>
                  <FormLabel htmlFor="confirm">{i18nSecurity('ConfirmPassword')}</FormLabel>
                  <Input id="confirm" type="password" {...register('confirm')} />
                  <ErrorMessage error={formState.errors.confirm} />
                </FormControl>
              </Stack>
            </Stack>
          </CardBody>
          <Divider />
          <CardFooter justifyContent="flex-end">
            <Button disabled={isDisabled} type="submit">
              {i18nGeneral('Submit')}
            </Button>
          </CardFooter>
        </Card>
      </Stack>
    </form>
  );
}

export type UpdateAddressFormProps = Props;
